/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React from "react";
import { PLATFORM } from "../../../config";
import ToggleSwitch from "../../../assets/component/ToggleSwitch/ToggleSwitch";
import UpgradeNoticeCard from "./UpgradeNoticeCard";
import ReceiptCanvas from "./ReceiptCanvas";
import ReceiptEditorToolbar from "./ReceiptEditorToolbar";
import ElementPropertyPanel from "./ElementPropertyPanel";
import LayersPanel from "./LayersPanel";
import ReceiptNumberConfig from "./ReceiptNumberConfig";
import ReceiptBackgroundPanel from "./ReceiptBackgroundPanel";

export function RenderOrgReceipt(state, func, _textObj) {
  if (!state || !func) return;
  const textObj = _textObj || {};

  if (state.loading) {
    return (
      <div className="OrgReceipt-emptyPage">
        <div className="OrgReceipt-center">
          <div className="OrgReceipt-btm-load"></div>
        </div>
      </div>
    );
  }

  const handleDonationClick = () => {
    func.setState({
      ...state,
      activeReceiptType: "donation"
    });
  };

  const handlePaymentClick = () => {
    func.setState({
      ...state,
      activeReceiptType: "payment"
    });
  };

  // autoReceipt defaults to true (current behavior) if not explicitly set
  const autoReceiptEnabled = state.organizationObj?.autoReceipt !== false;

  // Premium plan check
  const currentPlan = (state.organizationObj?.currentPlan || "").toLowerCase();
  const isPremium = currentPlan === "premium";

  // Compute next receipt number for preview
  const receiptNumberConfig = state.organizationObj?.receiptNumberConfig || {};
  const receiptCounter = state.receiptCounter || {};
  const activeType = state.activeReceiptType || 'donation';
  const typeCfg = receiptNumberConfig[activeType] || {};
  const defaultPrefix = activeType === 'donation' ? 'D' : 'P';
  const prefix = typeCfg.prefix !== undefined ? typeCfg.prefix : defaultPrefix;
  const digits = Math.min(Math.max(typeCfg.digits || 5, 3), 10);
  const nextNum = receiptCounter[activeType] || 1;
  const nextReceiptNumber = prefix + String(nextNum).padStart(digits, '0');

  // Get the selected element for the property panel
  const selectedElement = state.receiptTemplate
    ? (state.receiptTemplate.elements || []).find(
      (el) => el.id === state.selectedElementId
    )
    : null;

  return (
    <div className="OrgReceipt-contentContainer">
      <div className="OrgReceipt-contentHeader">
        <div className="OrgReceipt-info">
          {textObj.heading}
          {isPremium && !state.receiptEditorOpen && (
            <button
              type="button"
              className="OrgReceipt-editBtn"
              onClick={() => func.setState({ receiptEditorOpen: true })}
              title={textObj.customizeLayout || "Customize Receipt Layout"}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M12.146.854a.5.5 0 0 1 .708 0l2.292 2.292a.5.5 0 0 1 0 .708l-9.793 9.793a.5.5 0 0 1-.168.11l-3.5 1.5a.5.5 0 0 1-.65-.65l1.5-3.5a.5.5 0 0 1 .11-.168L12.146.854zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 3 10.707V11h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l7.5-7.5z"/>
              </svg>
            </button>
          )}
          {isPremium && state.receiptEditorOpen && (
            <button
              type="button"
              className="OrgReceipt-backBtn"
              onClick={() => func.setState({ receiptEditorOpen: false, selectedElementId: null })}
              title={textObj.backToPreview || "Back to Receipt Preview"}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/>
              </svg>
              {textObj.back || "Back"}
            </button>
          )}
        </div>
        <div className="OrgReceipt-autoReceiptSection">
          <div className="OrgReceipt-autoReceiptRow">
            <div className="OrgReceipt-autoReceiptInfo">
              <div className="OrgReceipt-autoReceiptTitle">
                {textObj.autoReceipt || "Automatic Receipt Issuing"}
                <span className="OrgReceipt-autoReceiptTooltip">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="#868686" style={{ verticalAlign: "middle", marginLeft: 6, cursor: "help" }}>
                    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 12.5a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11zM7.25 5h1.5v1.5h-1.5V5zm0 3h1.5v3h-1.5V8z"/>
                  </svg>
                  <span className="OrgReceipt-autoReceiptTooltip-text">
                    {autoReceiptEnabled
                      ? (textObj.autoReceiptOn || "Receipts are issued automatically when a payment is received.")
                      : (textObj.autoReceiptOff || "Receipts will only be issued when you manually trigger receipt issuing after confirming payment.")}
                  </span>
                </span>
              </div>
            </div>
            <ToggleSwitch
              checked={autoReceiptEnabled}
              onChange={func.handleToggleAutoReceipt}
            />
          </div>
        </div>
      </div>

      <div className="OrgReceipt-content">
        {/* Receipt preview / editor */}
        {isPremium && state.receiptEditorOpen ? (
          /* Editor mode for premium users */
          <div className="ReceiptEditor-container">
            <div className="ReceiptEditor-body">
              <ReceiptCanvas
                elements={
                  state.receiptTemplate
                    ? state.receiptTemplate.elements || []
                    : []
                }
                selectedElementId={state.selectedElementId}
                onSelectElement={func.handleSelectReceiptElement}
                onDragStop={func.handleReceiptElementDrag}
                onResizeStop={func.handleReceiptElementResize}
                onUpdateElement={func.handleUpdateReceiptElement}
                organizationObj={state.organizationObj}
                receiptType={state.activeReceiptType}
                background={state.receiptTemplate?.background}
                pageSize={state.organizationObj?.receiptPageSize || 'A4'}
                orientation={state.organizationObj?.receiptOrientation || 'portrait'}
                nextReceiptNumber={nextReceiptNumber}
                previewDonorType={state.previewDonorType || "individual"}
                onPreviewDonorTypeChange={func.handlePreviewDonorTypeChange}
              />
              <div className="ReceiptEditor-sidebar">
                {/* Toggle for receipt type */}
                {PLATFORM === "plug" ? null : (
                  <div className="OrgReceipt-typeToggle">
                    <div
                      className="OrgReceipt-typeToggleSlider"
                      style={{ transform: state.activeReceiptType === "payment" ? "translateX(100%)" : "translateX(0)" }}
                    />
                    <button
                      type="button"
                      className={`OrgReceipt-typeToggleBtn ${state.activeReceiptType === "donation" ? "OrgReceipt-typeToggleBtn--active" : ""}`}
                      onClick={handleDonationClick}
                    >
                      {textObj.donation}
                    </button>
                    <button
                      type="button"
                      className={`OrgReceipt-typeToggleBtn ${state.activeReceiptType === "payment" ? "OrgReceipt-typeToggleBtn--active" : ""}`}
                      onClick={handlePaymentClick}
                    >
                      {textObj.payment}
                    </button>
                  </div>
                )}

                {/* Receipt settings: page size & orientation */}
                <div className="ReceiptEditor-settingsPanel">
                  <div className="ReceiptEditor-settingsRow">
                    <div className="ReceiptEditor-settingsLabel">{textObj.pageSize || "Page Size"}</div>
                    <div className="OrgReceipt-pageSizeToggle">
                      <div
                        className={`OrgReceipt-pageSizeOption${(state.organizationObj?.receiptPageSize || 'A4') === 'A4' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                        onClick={() => func.handleReceiptPageSizeChange('A4')}
                      >
                        A4
                      </div>
                      <div
                        className={`OrgReceipt-pageSizeOption${state.organizationObj?.receiptPageSize === 'A5' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                        onClick={() => func.handleReceiptPageSizeChange('A5')}
                      >
                        A5
                      </div>
                    </div>
                  </div>
                  <div className="ReceiptEditor-settingsRow">
                    <div className="ReceiptEditor-settingsLabel">{textObj.orientation || "Orientation"}</div>
                    <div className="OrgReceipt-pageSizeToggle">
                      <div
                        className={`OrgReceipt-pageSizeOption${(state.organizationObj?.receiptOrientation || 'portrait') === 'portrait' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                        onClick={() => func.handleReceiptOrientationChange('portrait')}
                      >
                        {textObj.portrait || "Portrait"}
                      </div>
                      <div
                        className={`OrgReceipt-pageSizeOption${state.organizationObj?.receiptOrientation === 'landscape' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                        onClick={() => func.handleReceiptOrientationChange('landscape')}
                      >
                        {textObj.landscape || "Landscape"}
                      </div>
                    </div>
                  </div>
                  <div className="ReceiptEditor-settingsRow">
                    <div className="ReceiptEditor-settingsLabel">{textObj.previewAs || "Preview as"}</div>
                    <div className="OrgReceipt-pageSizeToggle">
                      <div
                        className={`OrgReceipt-pageSizeOption${(state.previewDonorType || 'individual') === 'individual' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                        onClick={() => func.handlePreviewDonorTypeChange('individual')}
                      >
                        {textObj.previewIndividual || "Individual"}
                      </div>
                      <div
                        className={`OrgReceipt-pageSizeOption${state.previewDonorType === 'organization' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                        onClick={() => func.handlePreviewDonorTypeChange('organization')}
                      >
                        {textObj.previewOrganisation || "Organisation"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Receipt background image */}
                <ReceiptBackgroundPanel
                  background={state.receiptTemplate?.background}
                  onUpload={func.handleUploadReceiptBackground}
                  onOpacityChange={func.handleReceiptBackgroundOpacity}
                  onRemove={func.handleRemoveReceiptBackground}
                  loading={state.loading}
                  textObj={textObj}
                />

                {/* Receipt number format */}
                <ReceiptNumberConfig
                  config={state.organizationObj && state.organizationObj.receiptNumberConfig}
                  onSave={func.handleReceiptNumberConfigChange}
                  inline
                  counter={state.receiptCounter}
                  textObj={textObj}
                />

                <ReceiptEditorToolbar
                  onAddText={func.handleAddReceiptText}
                  onAddImage={func.handleAddReceiptImage}
                  onSave={func.handleSaveReceiptTemplate}
                  onReset={func.handleResetReceiptTemplate}
                  onSendTest={func.handleSendTestReceipt}
                  loading={state.loading}
                  dirty={state.receiptEditorDirty}
                  textObj={textObj}
                />
                <LayersPanel
                  elements={
                    state.receiptTemplate
                      ? state.receiptTemplate.elements || []
                      : []
                  }
                  selectedElementId={state.selectedElementId}
                  onSelectElement={func.handleSelectReceiptElement}
                  onReorder={func.handleReorderReceiptElements}
                  onUpdateElement={func.handleUpdateReceiptElement}
                  onDelete={func.handleDeleteReceiptElement}
                  textObj={textObj}
                />
                <ElementPropertyPanel
                  element={selectedElement}
                  onUpdateElement={func.handleUpdateReceiptElement}
                  onDeleteElement={func.handleDeleteReceiptElement}
                  onUploadReceiptLogo={func.handleUploadReceiptLogo}
                  organizationObj={state.organizationObj}
                  receiptType={state.activeReceiptType}
                  previewDonorType={state.previewDonorType || "individual"}
                  textObj={textObj}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Toggle for receipt type - hidden for Plug platform */}
            {PLATFORM === "plug" ? null : (
              <div className="OrgReceipt-typeToggle">
                <div
                  className="OrgReceipt-typeToggleSlider"
                  style={{ transform: state.activeReceiptType === "payment" ? "translateX(100%)" : "translateX(0)" }}
                />
                <button
                  type="button"
                  className={`OrgReceipt-typeToggleBtn ${state.activeReceiptType === "donation" ? "OrgReceipt-typeToggleBtn--active" : ""}`}
                  onClick={handleDonationClick}
                >
                  {textObj.donation}
                </button>
                <button
                  type="button"
                  className={`OrgReceipt-typeToggleBtn ${state.activeReceiptType === "payment" ? "OrgReceipt-typeToggleBtn--active" : ""}`}
                  onClick={handlePaymentClick}
                >
                  {textObj.payment}
                </button>
              </div>
            )}

            {/* Receipt note */}
            <div className="OrgReceipt-note">
              {state.activeReceiptType === "donation"
                ? (textObj.receiptNote && textObj.receiptNote.donation) || ""
                : (textObj.receiptNote && textObj.receiptNote.payment) || ""}
            </div>

            {/* Receipt toolbar: number format | orientation | page size */}
            <div className="OrgReceipt-toolbarWrap">
              <div className="OrgReceipt-toolbar">
                <div className="OrgReceipt-toolbarLeft">
                  <ReceiptNumberConfig
                    config={state.organizationObj && state.organizationObj.receiptNumberConfig}
                    onSave={func.handleReceiptNumberConfigChange}
                    inline
                    counter={state.receiptCounter}
                    readOnly
                    textObj={textObj}
                  />
                </div>
                {isPremium && (
                  <div className="OrgReceipt-toolbarCenter">
                    <div className="OrgReceipt-toolbarLabel">{textObj.orientation || "Orientation"}</div>
                    <div className="OrgReceipt-pageSizeToggle">
                      <div
                        className={`OrgReceipt-pageSizeOption${(state.organizationObj?.receiptOrientation || 'portrait') === 'portrait' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                        onClick={() => func.handleReceiptOrientationChange('portrait')}
                      >
                        {textObj.portrait || "Portrait"}
                      </div>
                      <div
                        className={`OrgReceipt-pageSizeOption${state.organizationObj?.receiptOrientation === 'landscape' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                        onClick={() => func.handleReceiptOrientationChange('landscape')}
                      >
                        {textObj.landscape || "Landscape"}
                      </div>
                    </div>
                  </div>
                )}
                {isPremium && (
                  <div className="OrgReceipt-toolbarRight">
                    <div className="OrgReceipt-toolbarLabel">{textObj.pageSize || "Page Size"}</div>
                    <div className="OrgReceipt-pageSizeToggle">
                      <div
                        className={`OrgReceipt-pageSizeOption${(state.organizationObj?.receiptPageSize || 'A4') === 'A4' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                        onClick={() => func.handleReceiptPageSizeChange('A4')}
                      >
                        A4
                      </div>
                      <div
                        className={`OrgReceipt-pageSizeOption${state.organizationObj?.receiptPageSize === 'A5' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                        onClick={() => func.handleReceiptPageSizeChange('A5')}
                      >
                        A5
                      </div>
                    </div>
                  </div>
                )}
                <div className="OrgReceipt-toolbarPreview">
                  <div className="OrgReceipt-toolbarLabel">{textObj.previewAs || "Preview as"}</div>
                  <div className="OrgReceipt-pageSizeToggle">
                    <div
                      className={`OrgReceipt-pageSizeOption${(state.previewDonorType || 'individual') === 'individual' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                      onClick={() => func.handlePreviewDonorTypeChange('individual')}
                    >
                      {textObj.previewIndividual || "Individual"}
                    </div>
                    <div
                      className={`OrgReceipt-pageSizeOption${state.previewDonorType === 'organization' ? ' OrgReceipt-pageSizeOption--active' : ''}`}
                      onClick={() => func.handlePreviewDonorTypeChange('organization')}
                    >
                      {textObj.previewOrganisation || "Organisation"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Live receipt preview (read-only) */}
            <ReceiptCanvas
              elements={
                state.receiptTemplate
                  ? state.receiptTemplate.elements || []
                  : []
              }
              selectedElementId={null}
              onSelectElement={() => {}}
              onDragStop={() => {}}
              onResizeStop={() => {}}
              organizationObj={state.organizationObj}
              receiptType={state.activeReceiptType}
              background={state.receiptTemplate?.background}
              pageSize={state.organizationObj?.receiptPageSize || 'A4'}
              orientation={state.organizationObj?.receiptOrientation || 'portrait'}
              readOnly
              nextReceiptNumber={nextReceiptNumber}
              previewDonorType={state.previewDonorType || "individual"}
              onPreviewDonorTypeChange={func.handlePreviewDonorTypeChange}
            />

            {!isPremium && (
              <UpgradeNoticeCard
                heading={textObj.upgradeHeading || "Customize Receipt Layout"}
                body={textObj.upgradeBody || "Drag-and-drop receipt customization with custom text and images is available on the Premium plan. Upgrade to design your own receipt layout."}
                buttonLabel={textObj.upgradeBtn || "Upgrade Now"}
                onUpgradeClick={func.redirectToOrgBilling}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default RenderOrgReceipt;
